import { useCallback } from "react";
import { useApi } from "./useApi";
import { applyBlueprint, BlueprintApplyScope } from "./blueprintMutations";

// Wraps the applyBlueprint mutation for the Blueprint Manager deploy tabs. Callers trigger a deploy
// with `apply({ ... })` and observe `result` / `loading` / `errors` reactively. Deployment logic
// (standard reconciliation, instance remapping, column configs) lives entirely in the backend; this
// hook only assembles the request and surfaces the structured ApplyBlueprintResult.
//
// `blueprint` may be the parsed blueprint object (as loaded from a /blueprints/*.json file) or an
// already-serialized JSON string. Only its `sourceProfiles` are sent; file-level metadata (name,
// description, editedDate) is dropped — the backend contract is `{ sourceProfiles: [...] }`.
export function useApplyBlueprint() {
  const [{ data, errors, loading }, run, clear] = useApi();

  const apply = useCallback(
    ({
      blueprint,
      scope,
      sourcePairings = [],
      permissions = [],
      applyColumnConfigurations = false,
      renameOnConflict = false,
      dryRun = false,
    }) => {
      const blueprintJson =
        typeof blueprint === "string"
          ? blueprint
          : JSON.stringify({ sourceProfiles: blueprint?.sourceProfiles ?? [] });

      run({
        query: applyBlueprint,
        variables: {
          blueprint: blueprintJson,
          scope,
          sourcePairings,
          permissions,
          applyColumnConfigurations,
          renameOnConflict,
          dryRun,
        },
      });
    },
    [run]
  );

  return {
    apply,
    clear,
    result: data?.applyBlueprint ?? null,
    errors,
    loading,
  };
}

export { BlueprintApplyScope };
